import { ChangeEvent, type ReactElement, type ReactNode, useId } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  type SelectProps,
  Stack,
  Typography,
  TypographyProps,
} from "@mui/material";

import { CircleAlertIcon } from "lucide-react";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

type Option = {
  id: string;
  isDivider?: boolean;
  disabled?: boolean;
  padding?: number;
  label?: string;
  secondaryLabel?: ReactNode;
};

type RadioInputProps<TForm extends FieldValues, TOption extends Option> = {
  fieldName: Path<TForm>;
  label: string;
  hideLabel?: boolean;
  hideError?: boolean;
  control: Control<TForm>;
  size?: SelectProps["size"];
  optionLabelField?: keyof TOption;
  disabled?: boolean;
  options: TOption[];
  onChange?: (event: ChangeEvent, value: string) => void;
  direction?: "column" | "row";
  labelVariant?: TypographyProps["variant"];
};

export const RadioInput = <TForm extends FieldValues, TOption extends Option>({
  fieldName,
  label,
  hideLabel = false,
  hideError = false,
  control,
  size,
  optionLabelField = "label",
  disabled,
  options,
  onChange,
  direction = "column",
  labelVariant,
}: RadioInputProps<TForm, TOption>): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const id = useId();

  const {
    field: { ...field },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  return (
    <Box display={"flex"} flexDirection={"column"} gap={0.5} width={"100%"}>
      {!hideLabel && (
        <Typography variant={labelVariant} id={id}>
          {label}
        </Typography>
      )}
      <FormControl fullWidth disabled={disabled} error={!!error} size={size}>
        <RadioGroup
          onChange={(...args) => {
            field.onChange(...args);
            onChange?.(...args);
          }}
          value={field.value ?? ""}
          name={fieldName}
          aria-labelledby={id}
          sx={{ flexDirection: direction }}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.id}
              value={option.id}
              control={<Radio />}
              slotProps={{ typography: { variant: "bodyDefaultRegular" } }}
              label={option[optionLabelField]?.toString()}
            />
          ))}
        </RadioGroup>
        {!hideError && error?.message && (
          <Stack direction={"row"} gap={1} alignItems={"center"} paddingTop={1}>
            <Typography color={"danger.decoration"} height={"20px"}>
              <CircleAlertIcon size={20} />
            </Typography>
            <Typography variant={"bodySmallRegular"}>
              {i18n.exists(error.message)
                ? t(error.message as any)
                : error.message}
            </Typography>
          </Stack>
        )}
      </FormControl>
    </Box>
  );
};
